import { useTranslation } from "react-i18next";
import { URLS } from "../../../../services/urls";
import { useCallback, useEffect, useRef, useState } from "react";
import { styles } from "../../../../components/utils/reactSelect";
import { API } from "../../../../services/axios";
import { useSelector } from "react-redux";
import useSWR from "swr";
import CreatableSelect from "react-select/creatable";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmDeletion } from "../../../../components/dletion";
import ExperienceTimelineCard from "./components/ExperienceTimeLine";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ErrorMessage } from "@hookform/error-message";
import moment from "moment";

const ManageWorkExperience = ({ handleNext, handlePrev }) => {
  const { t }      = useTranslation();
  const { action } = useParams();
  const { auth }   = useSelector((state) => state.auth);

  const [experiences, setExperiences]   = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [isLoadingSectors, setIsLoadingSectors] = useState(false);
  const [toEdit, setToEdit]             = useState(null);
  const [loading, setLoading]           = useState(false);
  const [description, setDescription]   = useState("");
  const [search, setSearch]             = useState("");
  const [searchSectors, setSearchSectors] = useState("");
  const [selected, setSelected]         = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);

  const { data: companies, isLoading: loadingCompanies } = useSWR(URLS.CONFIGS.PROFILE.COMPANY.list(search));
  const { data: sectors,   isLoading: loadingSectors   } = useSWR(URLS.CONFIGS.PROFILE.SECTOR.list(searchSectors));

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({});
  const formRef = useRef(null);

  // ─── Fetch experiences directly from API ──────────────────────────────────
  const loadExperiences = useCallback(() => {
    if (!action || action === "create") return;
    setFetchLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => {
        setExperiences([...(data.experience || [])].reverse());
        setFetchLoading(false);
      })
      .catch(() => setFetchLoading(false));
  }, [action]);

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  // ─── Edit ──────────────────────────────────────────────────────────────────
  const handleChangeExperience = (experience) => {
    setToEdit(experience);
    reset({
      educationFrom:   experience.start_date,
      currentlyWorking: experience.current_job,
      educationTo:     experience.end_date,
      postTitle:       experience.title,
    });
    setSelected({ label: experience.company?.name, value: experience.company?.id });
    setSelectedSector({ label: experience.industry?.name, value: experience.industry?.id });
    setDescription(experience.description || "");
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteExperience = (experience) => {
    const onSuccess = () =>
      setExperiences((prev) => prev.filter((e) => e.id !== experience.id));
    confirmDeletion(URLS.USERS.PROFILE.EXPERIENCE.update(experience.id), t, onSuccess);
  };

  // ─── Company / Sector creation ─────────────────────────────────────────────
  const handleCreateCompany = (inputValue) => {
    setIsLoading(true);
    API.post(URLS.CONFIGS.PROFILE.COMPANY.create, {
      name: inputValue, created_by: auth?.user?.id, company_url: "www.example.com",
    })
      .then(({ data }) => { setSelected({ label: data.name, value: data.id }); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  const handleCreateSector = (inputValue) => {
    setIsLoadingSectors(true);
    API.post(URLS.CONFIGS.PROFILE.SECTOR.create, { name: inputValue, created_by: auth?.user?.id })
      .then(({ data }) => { setSelectedSector({ label: data.name, value: data.id }); setIsLoadingSectors(false); })
      .catch(() => setIsLoadingSectors(false));
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    if (!selected || !selectedSector) {
      toast.error(t("textCompanyOrSectorRequired") || "Entreprise et secteur requis");
      return;
    }
    setLoading(true);
    const obj = {
      start_date:   data.educationFrom,
      end_date:     data.currentlyWorking ? null : data.educationTo,
      title:        data.postTitle,
      current_job:  !!data.currentlyWorking,
      company:      selected.value,
      activity:     selectedSector.value,
      industry:     selectedSector.value,
      profile:      action,
      description:  description,
    };

    if (!toEdit) {
      API.post(URLS.USERS.PROFILE.EXPERIENCE.create, obj)
        .then(({ data: resp }) => {
          setLoading(false);
          reset();
          setDescription("");
          toast.success(t("experienceAdded"));
          setExperiences((prev) => [
            { ...resp, company: { name: selected.label }, industry: { name: selectedSector.label } },
            ...prev,
          ]);
          setSelected(null);
          setSelectedSector(null);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    } else {
      API.patch(URLS.USERS.PROFILE.EXPERIENCE.update(toEdit.id), obj)
        .then(({ data: resp }) => {
          setLoading(false);
          toast.success(t("experienceEdited"));
          setExperiences((prev) =>
            prev.map((e) =>
              e.id === toEdit.id
                ? { ...resp, company: { name: selected.label }, industry: { name: selectedSector.label } }
                : e
            )
          );
          setToEdit(null);
          reset();
          setDescription("");
          setSelected(null);
          setSelectedSector(null);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="panel-white mb-30" id="experience">
      <div className="box-padding">
        <h5 className="icon-edu">{t("workExperience")}</h5>
        <div className="row mt-30">
          <div className="col-lg-9">
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="row">
                {/* Start date */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("educationDe")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("educationFrom", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="date"
                    />
                    <ErrorMessage errors={errors} name="educationFrom"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* End date */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("educationTo")}</label>
                    {!watch("currentlyWorking") && (
                      <input
                        {...register("educationTo")}
                        className="form-control"
                        type="date"
                        max={moment().format("YYYY-MM-DD")}
                      />
                    )}
                    <div className="login_footer form-group mt-10">
                      <label className="cb-container">
                        <input type="checkbox" {...register("currentlyWorking")} />
                        <span className="text-small">{t("currentlyWorking")}</span>
                        <span className="checkmark" />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Job title */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("postTitle")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("postTitle", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("postTitle")}
                    />
                    <ErrorMessage errors={errors} name="postTitle"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* Company */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("experienceSociété")} <span className="text-primary">*</span>
                    </label>
                    <CreatableSelect
                      styles={styles}
                      isSearchable
                      isDisabled={isLoading}
                      inputId="select-company"
                      value={selected}
                      isLoading={isLoading || loadingCompanies}
                      onInputChange={(v) => setSearch(v)}
                      onCreateOption={handleCreateCompany}
                      onChange={(v) => setSelected(v)}
                      options={companies?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                      placeholder={t("textChoose")}
                    />
                  </div>
                </div>

                {/* Sector */}
                <div className="col-lg-12 col-md-12">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("experienceSector")} <span className="text-primary">*</span>
                    </label>
                    <CreatableSelect
                      styles={styles}
                      isSearchable
                      isDisabled={isLoadingSectors}
                      inputId="select-activity"
                      value={selectedSector}
                      isLoading={isLoadingSectors || loadingSectors}
                      onInputChange={(v) => setSearchSectors(v)}
                      onCreateOption={handleCreateSector}
                      onChange={(v) => setSelectedSector(v)}
                      options={sectors?.results?.map((i) => ({ label: i.name, value: i.id })) ?? []}
                      placeholder={t("textChoose")}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="col-lg-12">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textDescription")}</label>
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={setDescription}
                      style={{ height: "220px", marginBottom: "50px" }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="col-lg-12">
                  <div className="form-group mt-10 d-flex">
                    <button type="submit" className="btn btn-default btn-brand icon-tick ml-10">
                      {!toEdit ? t("textAddExperience") : t("textEditExperience")}
                      {loading && <span className="spinner-border spinner-border-sm ml-10" role="status" />}
                    </button>
                    {toEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary ml-10"
                        onClick={() => { setToEdit(null); reset(); setDescription(""); setSelected(null); setSelectedSector(null); }}
                      >
                        {t("deleteActionCancel")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* List */}
            {fetchLoading ? (
              <div className="d-flex mt-30">
                <div className="spinner-border m-auto spinner-border-sm" role="status" />
              </div>
            ) : (
              <div className="box-timeline mt-50">
                {experiences.map((experience) => (
                  <ExperienceTimelineCard
                    key={experience.id}
                    handleChange={handleChangeExperience}
                    handleDelete={handleDeleteExperience}
                    experience={experience}
                  />
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="col-lg-12 mt-3">
              <div className="form-group mt-10 d-flex">
                <button type="button" onClick={handlePrev} className="btn btn-secondary btn-brand line-height-24">
                  {t("textPrevious")}
                </button>
                <button type="button" onClick={handleNext} className="btn btn-default btn-brand ml-auto">
                  {t("textNext")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageWorkExperience;
